import { read, utils } from 'xlsx';
import { REFERENCE_TYPES } from './manifestEditor';

export type ReferenceColumnMap = {
  shippingReference?: string;
  vin?: string;
  referenceType?: string;
};

export type ReferenceVehicleRow = {
  reference: string;
  vin: string;
  referenceType?: string;
};

export type ImportedReference = {
  reference: string;
  referenceType?: string;
  vehicleCount: number;
  startVin: string;
  vins: string[];
};

export type ParsedReferenceSheet = {
  fileName: string;
  sheetName: string;
  headers: string[];
  rows: Record<string, string>[];
  suggested: ReferenceColumnMap;
  referenceFromSheetName: boolean;
  defaultReference?: string;
};

const VIN_HEADERS = [
  'vin',
  'vins',
  'fullvin',
  'vincode',
  'vehicleidentificationnumber',
  'chassis',
  'chassisno',
  'chassisnumber',
];

const REFERENCE_HEADERS = [
  'shippingreference',
  'shippingref',
  'referenceno',
  'referencenumber',
  'refno',
  'billoflading',
  'billofladingno',
  'blno',
  'bookingno',
  'bookingnumber',
  'reference',
];

const TYPE_HEADERS = ['referencetype', 'reftype'];

const TYPE_VALUES: Record<string, string> = {
  billoflading: 'BillOfLading',
  billofladingno: 'BillOfLading',
  bol: 'BillOfLading',
  bl: 'BillOfLading',
  bookingno: 'BookingNo',
  booking: 'BookingNo',
  bookingnumber: 'BookingNo',
  customerref: 'CustomerRef',
  customerreference: 'CustomerRef',
  customer: 'CustomerRef',
};

export const normalizeImportHeader = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]/g, '');

export const matchReferenceType = (value?: string | null) => {
  const key = normalizeImportHeader(value ?? '');
  if (!key) return undefined;
  return (
    TYPE_VALUES[key] ??
    REFERENCE_TYPES.find((type) => normalizeImportHeader(type.value) === key)
      ?.value
  );
};

const looksLikeVin = (value: string) => {
  const text = value.trim().toUpperCase();
  return (
    /^[A-Z0-9]{11,17}$/.test(text) && /\d/.test(text) && /[A-Z]/.test(text)
  );
};

const findHeader = (
  headers: string[],
  aliases: string[],
  used: Set<string>,
) => {
  const available = headers.filter(
    (header) => !used.has(normalizeImportHeader(header)),
  );
  const exact = available.find((header) =>
    aliases.includes(normalizeImportHeader(header)),
  );
  if (exact) return exact;
  return available.find((header) => {
    const key = normalizeImportHeader(header);
    return aliases.some((alias) => alias.length >= 6 && key.includes(alias));
  });
};

const columnValues = (rows: Record<string, string>[], header: string) =>
  rows.map((row) => row[header] ?? '');

export const suggestReferenceColumns = (
  headers: string[],
  rows: Record<string, string>[],
): ReferenceColumnMap => {
  const used = new Set<string>();
  const claim = (header?: string) => {
    if (!header) return;
    used.add(normalizeImportHeader(header));
  };

  const referenceType =
    findHeader(headers, TYPE_HEADERS, used) ??
    headers.find((header) => {
      if (used.has(normalizeImportHeader(header))) return false;
      const filled = columnValues(rows, header).filter((value) => value.trim());
      if (!filled.length) return false;
      const matched = filled.filter((value) =>
        matchReferenceType(value),
      ).length;
      return matched / filled.length >= 0.6;
    });
  claim(referenceType);

  let vin = findHeader(headers, VIN_HEADERS, used);
  if (!vin) {
    const ranked = headers
      .filter((header) => !used.has(normalizeImportHeader(header)))
      .map((header) => {
        const filled = columnValues(rows, header).filter((value) =>
          value.trim(),
        );
        const matched = filled.filter((value) => looksLikeVin(value)).length;
        return { header, ratio: filled.length ? matched / filled.length : 0 };
      })
      .filter((item) => item.ratio >= 0.5)
      .sort((left, right) => right.ratio - left.ratio);
    vin = ranked[0]?.header;
  }
  claim(vin);

  const shippingReference = findHeader(headers, REFERENCE_HEADERS, used);
  return { shippingReference, vin, referenceType };
};

export const readReferenceRecords = (
  fileName: string,
  records: Record<string, unknown>[],
): ParsedReferenceSheet => {
  const headers: string[] = [];
  const seen = new Set<string>();
  records.forEach((record) => {
    Object.keys(record).forEach((header) => {
      const name = String(header ?? '').trim();
      const key = normalizeImportHeader(name);
      if (!name || !key || seen.has(key)) return;
      seen.add(key);
      headers.push(name);
    });
  });
  const rows = records
    .map((record) => {
      const row: Record<string, string> = {};
      headers.forEach((header) => {
        const source = Object.keys(record).find(
          (name) =>
            normalizeImportHeader(name) === normalizeImportHeader(header),
        );
        row[header] = String(source ? record[source] : '').trim();
      });
      return row;
    })
    .filter((row) => headers.some((header) => row[header]));
  const suggested = suggestReferenceColumns(headers, rows);
  const vinOnly =
    Boolean(suggested.vin) &&
    headers.filter((header) => rows.some((row) => row[header]?.trim()))
      .length <= 1;
  return {
    fileName,
    sheetName: fileName,
    headers,
    rows,
    suggested,
    referenceFromSheetName: vinOnly,
    defaultReference: vinOnly ? fileName : undefined,
  };
};

export const parseReferenceFile = async (
  file: File,
): Promise<ParsedReferenceSheet> => {
  const workbook = read(await file.arrayBuffer(), { type: 'array' });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) return readReferenceRecords(file.name, []);
  const records = utils.sheet_to_json<Record<string, unknown>>(sheet, {
    defval: '',
    raw: false,
  });
  return readReferenceRecords(file.name, records);
};

const vinSortKey = (vin: string) => {
  const match = vin.match(/^(.*?)(\d+)$/);
  if (!match) return { prefix: vin, serial: Number.POSITIVE_INFINITY, vin };
  return { prefix: match[1], serial: Number(match[2]), vin };
};

export const summarizeReferenceRows = (
  rows: ReferenceVehicleRow[],
  fallbackType?: string,
): ImportedReference[] => {
  const groups = new Map<
    string,
    { reference: string; vins: Set<string>; types: Set<string> }
  >();
  rows.forEach((row) => {
    const reference = row.reference.trim();
    const vin = row.vin.trim().toUpperCase();
    if (!reference || !vin) return;
    const key = reference.toUpperCase();
    const group = groups.get(key) ?? {
      reference,
      vins: new Set<string>(),
      types: new Set<string>(),
    };
    group.vins.add(vin);
    const type = matchReferenceType(row.referenceType);
    if (type) group.types.add(type);
    groups.set(key, group);
  });

  return [...groups.values()].map((group) => {
    const vins = [...group.vins].sort((left, right) => {
      const a = vinSortKey(left);
      const b = vinSortKey(right);
      if (a.prefix !== b.prefix) return a.vin.localeCompare(b.vin);
      if (a.serial !== b.serial) return a.serial - b.serial;
      return a.vin.localeCompare(b.vin);
    });
    const referenceType =
      group.types.size === 1 ? [...group.types][0] : fallbackType || undefined;
    return {
      reference: group.reference,
      referenceType,
      vehicleCount: vins.length,
      startVin: vins[0] ?? '',
      vins,
    };
  });
};

export const resolveReferenceColumnMap = (
  sheet: Pick<ParsedReferenceSheet, 'suggested'>,
  override?: ReferenceColumnMap,
): ReferenceColumnMap => ({
  ...sheet.suggested,
  ...override,
});

export const rowsFromColumnMap = (
  rows: Record<string, string>[],
  map: ReferenceColumnMap,
  fallbackReference?: string,
): ReferenceVehicleRow[] => {
  if (!map.vin || (!map.shippingReference && !fallbackReference)) return [];
  return rows.map((row) => ({
    reference: map.shippingReference
      ? (row[map.shippingReference] ?? '')
      : (fallbackReference ?? ''),
    vin: row[map.vin as string] ?? '',
    referenceType: map.referenceType ? row[map.referenceType] : undefined,
  }));
};

export const columnName = (index: number) => {
  let cursor = index;
  let name = '';
  do {
    name = String.fromCharCode((cursor % 26) + 65) + name;
    cursor = Math.floor(cursor / 26) - 1;
  } while (cursor >= 0);
  return `Column ${name}`;
};

const cellText = (value: unknown) => String(value ?? '').trim();

export const matrixToRecords = (matrix: unknown[][]) => {
  const grid = matrix
    .map((row) => (Array.isArray(row) ? row : []).map(cellText))
    .filter((row) => row.some(Boolean));
  if (!grid.length) return [];
  const width = Math.max(...grid.map((row) => row.length));
  const first = grid[0];
  const filled = first.filter(Boolean);
  const headerLike =
    filled.length > 0 &&
    filled.filter((value) => looksLikeVin(value)).length / filled.length < 0.5;
  const headers = Array.from({ length: width }, (_, index) =>
    headerLike ? first[index] || columnName(index) : columnName(index),
  );
  const body = headerLike ? grid.slice(1) : grid;
  return body.map((row) => {
    const record: Record<string, string> = {};
    headers.forEach((header, index) => {
      record[header] = row[index] ?? '';
    });
    return record;
  });
};

export const parseReferenceWorkbook = async (
  file: File,
): Promise<ParsedReferenceSheet[]> => {
  const workbook = read(await file.arrayBuffer(), { type: 'array' });
  return workbook.SheetNames.flatMap((sheetName) => {
    const sheet = workbook.Sheets[sheetName];
    if (!sheet) return [];
    const matrix = utils.sheet_to_json<unknown[]>(sheet, {
      header: 1,
      defval: '',
      raw: false,
    });
    const parsed = readReferenceRecords(sheetName, matrixToRecords(matrix));
    return [
      {
        ...parsed,
        fileName: `${file.name} · ${sheetName}`,
        sheetName,
        defaultReference: parsed.referenceFromSheetName ? sheetName : undefined,
      },
    ];
  });
};

export const parseVinList = async (file: File) => {
  const sheets = await parseReferenceWorkbook(file);
  const sheet = sheets.find((item) => item.suggested.vin);
  if (!sheet?.suggested.vin) return [];
  const column = sheet.suggested.vin;
  return [
    ...new Set(
      sheet.rows
        .map((row) => row[column]?.trim().toUpperCase() ?? '')
        .filter((vin) => looksLikeVin(vin)),
    ),
  ];
};

export const applyImportedReferences = <
  T extends {
    key: string;
    reference: string;
    referenceType?: string | null;
    vehicleCount: number;
    startVin?: string | null;
    vins?: string[];
  },
>(
  current: T[],
  imported: ImportedReference[],
  create: (item: ImportedReference) => T,
): T[] => {
  const next = [...current];
  imported.forEach((item) => {
    const index = next.findIndex(
      (row) =>
        row.reference.trim().toUpperCase() ===
        item.reference.trim().toUpperCase(),
    );
    if (index < 0) {
      next.push(create(item));
      return;
    }
    next[index] = {
      ...next[index],
      vehicleCount: item.vehicleCount,
      startVin: item.startVin,
      vins: item.vins,
      referenceType: item.referenceType || next[index].referenceType,
    };
  });
  return next;
};
